import React, { useState } from 'react';
import api from '../services/api';
import InputNombre from '../components/InputNombre';
import './RetourVente.css';

interface LigneVente {
  id: number;
  produitId: number;
  produitNom: string;
  quantite: number;
  prixUnitaire: number;
  remise: number;
  sousTotal: number;
}

interface Vente {
  id: number;
  numero: string;
  clientNom: string;
  totalTTC: number;
  createdAt: string;
  lignes: LigneVente[];
}

interface LigneRetour {
  produitId: number;
  produitNom: string;
  quantiteVendue: number;
  quantiteRetour: number;
  prixUnitaire: number;
  remise: number;
  selectionne: boolean;
  motif: string;
}

const fmt = (n: number) => Math.round(Number(n)).toLocaleString('fr-FR');

interface Props {
  vente: Vente;
  onFermer: () => void;
  onRetourEffectue: () => void;
}

export default function RetourVente({ vente, onFermer, onRetourEffectue }: Props) {
  const [lignes, setLignes] = useState<LigneRetour[]>(
    vente.lignes.map(l => ({
      produitId: l.produitId,
      produitNom: l.produitNom,
      quantiteVendue: l.quantite,
      quantiteRetour: 1,
      prixUnitaire: l.prixUnitaire,
      remise: l.remise,
      selectionne: false,
      motif: '',
    }))
  );
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState('');
  const [resultat, setResultat] = useState<any>(null);

  const toggleSelection = (index: number) => {
    setLignes(prev => prev.map((l, i) =>
      i === index ? { ...l, selectionne: !l.selectionne } : l
    ));
  };

  const modifierQuantite = (index: number, val: number) => {
    setLignes(prev => prev.map((l, i) =>
      i === index ? { ...l, quantiteRetour: Math.min(val, l.quantiteVendue) } : l
    ));
  };

  const modifierMotif = (index: number, motif: string) => {
    setLignes(prev => prev.map((l, i) =>
      i === index ? { ...l, motif } : l
    ));
  };

  const lignesSelectionnees = lignes.filter(l => l.selectionne);

  const montantTotal = lignesSelectionnees.reduce((s, l) => {
    return s + (l.prixUnitaire * (1 - l.remise / 100) * l.quantiteRetour);
  }, 0);

  const confirmerRetour = async () => {
    if (lignesSelectionnees.length === 0) {
      setErreur('Sélectionnez au moins un produit à retourner');
      return;
    }
    setChargement(true);
    setErreur('');
    try {
      const res = await api.post(`/ventes/${vente.id}/retour`, {
        lignes: lignesSelectionnees.map(l => ({
          produitId: l.produitId,
          quantite: l.quantiteRetour,
          motif: l.motif || 'Retour client',
        })),
      });
      setResultat(res.data);
      onRetourEffectue();
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Erreur lors du retour');
    } finally {
      setChargement(false);
    }
  };

  return (
    <div className="retour-overlay">
      <div className="retour-container">
        <div className="retour-header">
          <div>
            <h2>↩️ Retour produits</h2>
            <div className="retour-vente-info">
              Vente {vente.numero} —
              {vente.clientNom || 'Client de passage'} —
              {new Date(vente.createdAt).toLocaleDateString('fr-FR')}
            </div>
          </div>
          <button className="retour-close" onClick={onFermer}>×</button>
        </div>

        {!resultat ? (
          <>
            <div className="retour-body">
              {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

              <div className="retour-instructions">
                Cochez les produits à retourner et indiquez les quantités.
              </div>

              <div className="retour-lignes">
                {lignes.map((l, i) => (
                  <div key={i} className={`retour-ligne ${l.selectionne ? 'selectionnee' : ''}`}>
                    <div className="rl-top">
                      <label className="rl-check">
                        <input
                          type="checkbox"
                          checked={l.selectionne}
                          onChange={() => toggleSelection(i)}
                        />
                        <span className="rl-nom">{l.produitNom}</span>
                      </label>
                      <div className="rl-vendu">
                        Vendu : {l.quantiteVendue} — {fmt(l.prixUnitaire)} FCFA
                      </div>
                    </div>

                    {l.selectionne && (
                      <div className="rl-detail">
                        <div className="rl-qte-group">
                          <label>Quantité à retourner</label>
                          <div className="rl-qte-input">
                            <button onClick={() => modifierQuantite(i, Math.max(1, l.quantiteRetour - 1))}>−</button>
                            <InputNombre
                              value={l.quantiteRetour}
                              onChange={val => modifierQuantite(i, val)}
                              min={1}
                              max={l.quantiteVendue}
                              placeholder="1"
                            />
                            <button onClick={() => modifierQuantite(i, Math.min(l.quantiteVendue, l.quantiteRetour + 1))}>+</button>
                          </div>
                          <div className="rl-max">Max : {l.quantiteVendue}</div>
                        </div>

                        <div className="rl-motif-group">
                          <label>Motif du retour</label>
                          <select
                            value={l.motif}
                            onChange={e => modifierMotif(i, e.target.value)}>
                            <option value="">— Choisir —</option>
                            <option value="Produit défectueux">Produit défectueux</option>
                            <option value="Produit non conforme">Produit non conforme</option>
                            <option value="Erreur de commande">Erreur de commande</option>
                            <option value="Client insatisfait">Client insatisfait</option>
                            <option value="Produit endommagé">Produit endommagé</option>
                            <option value="Retour volontaire">Retour volontaire</option>
                            <option value="Autre">Autre</option>
                          </select>
                        </div>

                        <div className="rl-remboursement">
                          Remboursement : <strong>{fmt(l.prixUnitaire * (1 - l.remise / 100) * l.quantiteRetour)} FCFA</strong>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {lignesSelectionnees.length > 0 && (
                <div className="retour-total">
                  <div className="rt-ligne">
                    <span>{lignesSelectionnees.length} produit{lignesSelectionnees.length > 1 ? 's' : ''} sélectionné{lignesSelectionnees.length > 1 ? 's' : ''}</span>
                    <span className="rt-montant">{fmt(montantTotal)} FCFA à rembourser</span>
                  </div>
                  <div className="rt-note">
                    ⚠️ Le stock sera remis à jour automatiquement
                  </div>
                </div>
              )}
            </div>

            <div className="retour-footer">
              <button className="btn-secondary" onClick={onFermer}>Annuler</button>
              <button
                className="btn-retour"
                onClick={confirmerRetour}
                disabled={chargement || lignesSelectionnees.length === 0}>
                {chargement ? '⏳ Traitement...' : '↩️ Confirmer le retour'}
              </button>
            </div>
          </>
        ) : (
          <div className="retour-succes">
            <div className="rs-icone">✅</div>
            <div className="rs-titre">Retour enregistré avec succès</div>
            <div className="rs-details">
              <div className="rs-row">
                <span>Vente</span>
                <span>{resultat.venteNumero}</span>
              </div>
              <div className="rs-row">
                <span>Produits retournés</span>
                <span>{resultat.lignesRetournees}</span>
              </div>
              <div className="rs-row total">
                <span>Montant à rembourser</span>
                <span>{fmt(resultat.montantRembourse)} FCFA</span>
              </div>
            </div>
            <div className="rs-note">
              📦 Le stock a été remis à jour automatiquement
            </div>
            <button className="btn-retour" onClick={onFermer}>
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}